import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import '../i18n';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { getApiBaseUrl } from '../lib/utils';
import { FREE_FLASHCARD_LIMIT, getUpgradeCopy } from '../lib/subscription';
import { SUPPORTED_LANGUAGES, LANGUAGE_SELF_NAMES } from '../lib/languages';
import { LANGUAGE_LOCALE_CODES } from '../lib/localeMatch';
import { track } from '../lib/analytics';
import Select from './ui/Select';
import {
  fetchWebCheckoutUrl,
  fetchWebManageUrl,
  refreshWebSubscription
} from '../lib/webSubscription';

const PROFICIENCY_LEVELS = [1, 2, 3, 4, 5];

function AccountPage() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { user, isAuthenticated, loading, getToken, refreshUser, logout } = useAuth();
  const subscription = user?.subscription;
  const quota = user?.flashcardQuota;
  const isPremium = !!subscription?.hasPremium;
  const hasLinkedWebSubscription = subscription?.subscriptionPlatform === 'web' && !!subscription?.stripeSubscriptionId;
  const subscriptionDate = subscription?.stripeCancelsAt || subscription?.subscriptionRenewsAt;
  const subscriptionDateLabel = subscription?.stripeCancelsAt || subscription?.stripeCancelAtPeriodEnd
    ? t('account.subscription.cancelsOn')
    : t('account.subscription.renewsOn');

  const [email, setEmail] = useState('');
  const [nativeLanguage, setNativeLanguage] = useState('English');
  const [studyLanguage, setStudyLanguage] = useState('Mandarin');
  const [studyLanguageProficiency, setStudyLanguageProficiency] = useState(1);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showClearModal, setShowClearModal] = useState(false);
  const [clearConfirmText, setClearConfirmText] = useState('');
  const [clearLoading, setClearLoading] = useState(false);
  const [clearError, setClearError] = useState(null);
  const [showDeleteAccountModal, setShowDeleteAccountModal] = useState(false);
  const [deleteAccountConfirmText, setDeleteAccountConfirmText] = useState('');
  const [deleteAccountLoading, setDeleteAccountLoading] = useState(false);
  const [deleteAccountError, setDeleteAccountError] = useState(null);
  const [showLangWarning, setShowLangWarning] = useState(false);
  const [langWarningCount, setLangWarningCount] = useState(0);
  const [pendingPayload, setPendingPayload] = useState(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/login');
    }
  }, [loading, isAuthenticated, navigate]);

  useEffect(() => {
    if (user) {
      setEmail(user.email || '');
      setNativeLanguage(user.nativeLanguage || 'English');
      setStudyLanguage(user.studyLanguage || 'Mandarin');
      setStudyLanguageProficiency(user.studyLanguageProficiency || 1);
    }
  }, [user]);

  const buildPayload = () => {
    const payload = {};
    if (email !== user.email) payload.email = email;
    if (nativeLanguage !== user.nativeLanguage) payload.nativeLanguage = nativeLanguage;
    if (studyLanguage !== user.studyLanguage) payload.studyLanguage = studyLanguage;
    if (studyLanguageProficiency !== user.studyLanguageProficiency) payload.studyLanguageProficiency = studyLanguageProficiency;
    return payload;
  };

  const savePayload = async (payload) => {
    setSaving(true);
    try {
      await axios.put(
        `${getApiBaseUrl()}/api/auth/account`,
        payload,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      setSuccess(t('account.actions.updatedSuccessfully'));
      await refreshUser();
    } catch (err) {
      setError(err.response?.data?.error || t('account.actions.failedToUpdate'));
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    const payload = buildPayload();
    const languageChanged = payload.nativeLanguage || payload.studyLanguage;

    if (languageChanged) {
      try {
        const res = await axios.get(
          `${getApiBaseUrl()}/api/flashcards/count`,
          { headers: { Authorization: `Bearer ${getToken()}` } }
        );
        if (res.data.count > 0) {
          setLangWarningCount(res.data.count);
          setPendingPayload(payload);
          setShowLangWarning(true);
          return;
        }
      } catch {
        // If count fails, proceed without warning
      }
    }

    await savePayload(payload);
  };

  const confirmLanguageSwitch = async () => {
    setShowLangWarning(false);
    if (pendingPayload) {
      await savePayload(pendingPayload);
      setPendingPayload(null);
    }
  };

  const cancelLanguageSwitch = () => {
    setShowLangWarning(false);
    setPendingPayload(null);
  };

  const handleClearAll = async () => {
    setClearLoading(true);
    setClearError(null);

    try {
      await axios.delete(
        `${getApiBaseUrl()}/api/flashcards/clear-all`,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );

      setShowClearModal(false);
      setClearConfirmText('');
      setSuccess(t('account.dangerZone.clearedMessage'));
    } catch (err) {
      setClearError(err.response?.data?.error || t('account.dangerZone.failedToClear'));
    } finally {
      setClearLoading(false);
    }
  };

  const closeClearModal = () => {
    setShowClearModal(false);
    setClearConfirmText('');
    setClearError(null);
  };

  const handleDeleteAccount = async () => {
    setDeleteAccountLoading(true);
    setDeleteAccountError(null);
    try {
      await axios.delete(
        `${getApiBaseUrl()}/api/auth/account`,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      logout();
      navigate('/login');
    } catch (err) {
      setDeleteAccountError(err.response?.data?.error || t('account.dangerZone.failedToDelete'));
    } finally {
      setDeleteAccountLoading(false);
    }
  };

  const closeDeleteAccountModal = () => {
    setShowDeleteAccountModal(false);
    setDeleteAccountConfirmText('');
    setDeleteAccountError(null);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleUpgrade = async () => {
    setSubscriptionLoading(true);
    setError(null);
    try {
      const response = await fetchWebCheckoutUrl(getToken());
      track('purchase_started', { platform: 'web' });
      window.location.href = response.url;
    } catch (err) {
      setError(err.response?.data?.error || t('account.subscription.failedToStartCheckout'));
      setSubscriptionLoading(false);
    }
  };

  const handleManageSubscription = async () => {
    setSubscriptionLoading(true);
    setError(null);
    try {
      const response = await fetchWebManageUrl(getToken());
      window.open(response.url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      setError(err.response?.data?.error || t('account.subscription.failedToOpenManagement'));
    } finally {
      setSubscriptionLoading(false);
    }
  };

  const handleExport = async (format) => {
    try {
      const res = await axios.get(
        `${getApiBaseUrl()}/api/flashcards/export`,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      const flashcards = res.data.flashcards;

      let content, mimeType, filename;

      if (format === 'json') {
        content = JSON.stringify(flashcards, null, 2);
        mimeType = 'application/json';
        filename = 'flashcards.json';
      } else {
        const header = 'nativeText,studyTextPrimary,studyTextSecondary,priority,proficiency';
        const rows = flashcards.map(f => {
          const escape = v => v != null ? `"${String(v).replace(/"/g, '""')}"` : '';
          return [
            escape(f.nativeText),
            escape(f.studyTextPrimary),
            escape(f.studyTextSecondary),
            f.priority,
            f.proficiency
          ].join(',');
        });
        content = [header, ...rows].join('\n');
        mimeType = 'text/csv';
        filename = 'flashcards.csv';
      }

      const blob = new Blob([content], { type: mimeType });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(t('account.dataExport.failedToExport'));
    }
  };

  const handleRefreshSubscription = async () => {
    setSubscriptionLoading(true);
    setError(null);
    setSuccess(null);
    try {
      await refreshWebSubscription(getToken());
      await refreshUser();
      setSuccess(t('account.subscription.syncedSuccessfully'));
    } catch (err) {
      setError(err.response?.data?.error || t('account.subscription.failedToSync'));
    } finally {
      setSubscriptionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background py-8 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="bg-card rounded-lg shadow-xl border border-border p-4 sm:p-8">
          <h1 className="text-2xl font-bold text-foreground mb-6">{t('account.pageTitle')}</h1>

          <section className="mb-8">
            <h2 className="text-lg font-semibold text-foreground mb-4 pb-2 border-b border-border">
              {t('account.subscription.sectionTitle')}
            </h2>
            <div className={`rounded-lg border p-4 ${isPremium ? 'border-success/40 bg-success/10' : 'border-primary/30 bg-primary/5'}`}>
              <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <div className="space-y-1">
                  <p className="text-sm font-semibold text-foreground">
                    {isPremium ? t('account.subscription.premiumActive') : t('account.subscription.freePlan')}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {isPremium
                      ? t('account.subscription.unlimitedFlashcardsWeb', {
                          via: subscription?.subscriptionPlatform
                            ? t('account.subscription.viaSuffix', { platform: subscription.subscriptionPlatform })
                            : ''
                        })
                      : getUpgradeCopy()}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {isPremium
                      ? t('account.subscription.totalFlashcards', { count: quota?.totalCount ?? 0 })
                      : `${t('account.subscription.usedOfLimit', { used: quota?.totalCount ?? 0, limit: quota?.limit ?? FREE_FLASHCARD_LIMIT })}${quota?.remaining !== undefined ? t('account.subscription.remaining', { count: quota.remaining }) : ''}`}
                  </p>
                  {subscriptionDate && (
                    <p className="text-xs text-muted-foreground">
                      {subscriptionDateLabel} {new Date(subscriptionDate).toLocaleDateString()}
                    </p>
                  )}
                </div>
                <div className="flex flex-col gap-2">
                  {!isPremium && (
                    <button
                      type="button"
                      data-track="subscribe"
                      onClick={handleUpgrade}
                      disabled={subscriptionLoading}
                      className="flex items-center justify-center gap-1.5 text-sm font-semibold bg-warning text-black px-4 py-2 rounded-lg hover:bg-warning/90 transition-colors whitespace-nowrap disabled:opacity-50"
                    >
                      {!subscriptionLoading && (
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                          <path fillRule="evenodd" d="M9 4.5a.75.75 0 01.721.544l.813 2.846a3.75 3.75 0 002.576 2.576l2.846.813a.75.75 0 010 1.442l-2.846.813a3.75 3.75 0 00-2.576 2.576l-.813 2.846a.75.75 0 01-1.442 0l-.813-2.846a3.75 3.75 0 00-2.576-2.576l-2.846-.813a.75.75 0 010-1.442l2.846-.813A3.75 3.75 0 007.466 7.89l.813-2.846A.75.75 0 019 4.5zM18 1.5a.75.75 0 01.728.568l.258 1.036c.236.94.97 1.674 1.91 1.91l1.036.258a.75.75 0 010 1.456l-1.036.258c-.94.236-1.674.97-1.91 1.91l-.258 1.036a.75.75 0 01-1.456 0l-.258-1.036a2.625 2.625 0 00-1.91-1.91l-1.036-.258a.75.75 0 010-1.456l1.036-.258a2.625 2.625 0 001.91-1.91l.258-1.036A.75.75 0 0118 1.5zM16.5 15a.75.75 0 01.712.513l.394 1.183c.15.447.5.799.948.948l1.183.395a.75.75 0 010 1.422l-1.183.395c-.447.15-.799.5-.948.948l-.395 1.183a.75.75 0 01-1.422 0l-.395-1.183a1.5 1.5 0 00-.948-.948l-1.183-.395a.75.75 0 010-1.422l1.183-.395c.447-.15.799-.5.948-.948l.395-1.183A.75.75 0 0116.5 15z" clipRule="evenodd" />
                        </svg>
                      )}
                      {subscriptionLoading ? t('account.subscription.loadingButton') : t('account.subscription.upgradeToPremium')}
                    </button>
                  )}
                  {hasLinkedWebSubscription && (
                    <>
                      <button
                        type="button"
                        onClick={handleManageSubscription}
                        disabled={subscriptionLoading}
                        className="px-4 py-3 border border-border text-foreground rounded-lg hover:bg-muted transition-colors whitespace-nowrap disabled:opacity-50"
                      >
                        {t('account.subscription.manageSubscription')}
                      </button>
                      <button
                        type="button"
                        onClick={handleRefreshSubscription}
                        disabled={subscriptionLoading}
                        className="px-4 py-3 border border-border text-foreground rounded-lg hover:bg-muted transition-colors whitespace-nowrap disabled:opacity-50"
                      >
                        {t('account.subscription.syncSubscription')}
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </section>

          <form onSubmit={handleSubmit} className="space-y-8">
            <section>
              <h2 className="text-lg font-semibold text-foreground mb-4 pb-2 border-b border-border">
                {t('account.profile.sectionTitle')}
              </h2>
              <div className="space-y-4">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-foreground mb-2">
                    {t('account.profile.email')}
                  </label>
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full px-4 py-3 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-foreground"
                    required
                    disabled={saving}
                  />
                </div>

                <div>
                  <label htmlFor="nativeLanguage" className="block text-sm font-medium text-foreground mb-2">
                    {t('account.profile.nativeLanguage')}
                  </label>
                  <Select
                    id="nativeLanguage"
                    value={nativeLanguage}
                    onChange={(e) => {
                      const lang = e.target.value;
                      setNativeLanguage(lang);
                      const code = LANGUAGE_LOCALE_CODES[lang];
                      if (code) i18n.changeLanguage(code);
                    }}
                    disabled={saving}
                    iconTestId="native-language-chevron"
                  >
                    {SUPPORTED_LANGUAGES.map((lang) => (
                      <option key={lang} value={lang}>{LANGUAGE_SELF_NAMES[lang]}</option>
                    ))}
                  </Select>
                </div>

                <div>
                  <label htmlFor="studyLanguage" className="block text-sm font-medium text-foreground mb-2">
                    {t('account.profile.studyLanguage')}
                  </label>
                  <Select
                    id="studyLanguage"
                    value={studyLanguage}
                    onChange={(e) => setStudyLanguage(e.target.value)}
                    disabled={saving}
                    iconTestId="study-language-chevron"
                  >
                    {SUPPORTED_LANGUAGES.map((lang) => (
                      <option key={lang} value={lang}>{t(`languages.${lang}`)}</option>
                    ))}
                  </Select>
                </div>

                <div>
                  <label htmlFor="studyLanguageProficiency" className="block text-sm font-medium text-foreground mb-2">
                    {t('account.profile.proficiencyLevel')}
                  </label>
                  <Select
                    id="studyLanguageProficiency"
                    value={studyLanguageProficiency}
                    onChange={(e) => setStudyLanguageProficiency(Number(e.target.value))}
                    disabled={saving}
                    iconTestId="study-language-proficiency-chevron"
                  >
                    {PROFICIENCY_LEVELS.map((level) => (
                      <option key={level} value={level}>{level} — {t(`proficiency.${level}`)}</option>
                    ))}
                  </Select>
                  <p className="text-xs text-muted-foreground mt-1">
                    {t('account.profile.proficiencyHint')}
                  </p>
                </div>
              </div>
            </section>

            {error && (
              <div className="bg-destructive/20 border border-destructive text-destructive-foreground px-4 py-3 rounded">
                {error}
              </div>
            )}

            {success && (
              <div className="bg-success/20 border border-success text-success px-4 py-3 rounded">
                {success}
              </div>
            )}

            <div className="flex gap-4">
              <button
                type="submit"
                disabled={saving}
                className="flex-1 bg-primary text-primary-foreground py-3 rounded-lg font-semibold hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {saving ? t('languageSetup.saving') : t('account.actions.saveChanges')}
              </button>
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="px-6 py-3 border border-border text-foreground rounded-lg hover:bg-muted transition-colors"
              >
                {t('account.actions.cancel')}
              </button>
            </div>
          </form>

          <section className="mt-8 pt-8 border-t border-border">
            <h2 className="text-lg font-semibold text-foreground mb-4">{t('account.dataExport.sectionTitle')}</h2>
            <div className="border border-border rounded-lg p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <p className="text-sm font-medium text-foreground">{t('account.dataExport.download')}</p>
                <p className="text-xs text-muted-foreground">{t('account.dataExport.description')}</p>
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => handleExport('json')}
                  className="px-4 py-2 border border-border text-foreground rounded-lg text-sm font-semibold hover:bg-muted transition-colors whitespace-nowrap"
                >
                  {t('account.dataExport.json')}
                </button>
                <button
                  type="button"
                  onClick={() => handleExport('csv')}
                  className="px-4 py-2 border border-border text-foreground rounded-lg text-sm font-semibold hover:bg-muted transition-colors whitespace-nowrap"
                >
                  {t('account.dataExport.csv')}
                </button>
              </div>
            </div>
          </section>

          <section className="mt-8 pt-8 border-t border-destructive/30">
            <h2 className="text-lg font-semibold text-destructive mb-4">{t('account.dangerZone.sectionTitle')}</h2>
            <div className="space-y-3">
              <div className="border border-destructive/30 rounded-lg p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <p className="text-sm font-medium text-foreground">{t('account.dangerZone.clearAllTitle')}</p>
                  <p className="text-xs text-muted-foreground">{t('account.dangerZone.clearAllDescription')}</p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowClearModal(true)}
                  className="px-4 py-2 bg-destructive text-destructive-foreground rounded-lg text-sm font-semibold hover:bg-destructive/90 transition-colors whitespace-nowrap self-start sm:self-auto"
                >
                  {t('account.dangerZone.clearAll')}
                </button>
              </div>
              <div className="border border-destructive/30 rounded-lg p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <p className="text-sm font-medium text-foreground">{t('account.dangerZone.deleteAccountTitle')}</p>
                  <p className="text-xs text-muted-foreground">{t('account.dangerZone.deleteAccountDescription')}</p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowDeleteAccountModal(true)}
                  className="px-4 py-2 bg-destructive text-destructive-foreground rounded-lg text-sm font-semibold hover:bg-destructive/90 transition-colors whitespace-nowrap self-start sm:self-auto"
                >
                  {t('account.dangerZone.deleteAccount')}
                </button>
              </div>
            </div>
          </section>

          <section className="mt-8 pt-8 border-t border-border md:hidden">
            <Link
              to="/feedback"
              className="flex items-center gap-3 w-full bg-background border border-border rounded-lg p-4 hover:bg-muted transition-colors"
              data-testid="mobile-feedback-link"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-primary shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
              </svg>
              <div className="flex-1 text-left">
                <p className="text-sm font-semibold text-foreground">{t('account.mobile.feedbackSection.title')}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{t('account.mobile.feedbackSection.subtitle')}</p>
              </div>
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-muted-foreground shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Link>

            <button
              type="button"
              onClick={handleLogout}
              className="mt-4 w-full flex items-center justify-center gap-2 bg-destructive/10 border border-destructive/30 text-destructive rounded-lg py-3 font-semibold hover:bg-destructive/20 transition-colors"
              data-testid="mobile-signout-button"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              {t('account.mobile.signOut')}
            </button>
          </section>
        </div>

        {showClearModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-card rounded-xl shadow-xl border border-border w-full max-w-md p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-bold text-destructive">{t('account.modals.clearFlashcardsTitle')}</h3>
                <button
                  onClick={closeClearModal}
                  className="text-muted-foreground hover:text-foreground"
                  disabled={clearLoading}
                >
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <p className="text-sm text-muted-foreground mb-4">
                {t('account.modals.clearFlashcardsWarning')}
              </p>

              <p className="text-sm text-foreground mb-2">
                {t('account.modals.typeDeleteToConfirm')}
              </p>
              <input
                type="text"
                value={clearConfirmText}
                onChange={(e) => setClearConfirmText(e.target.value)}
                placeholder={t('account.modals.deletePlaceholder')}
                className="w-full px-4 py-3 bg-background border border-border rounded-lg focus:ring-2 focus:ring-destructive focus:border-transparent text-foreground mb-4"
                disabled={clearLoading}
                data-testid="clear-confirm-input"
              />

              {clearError && (
                <div className="bg-destructive/20 border border-destructive text-destructive-foreground px-3 py-2 rounded text-sm mb-4">
                  {clearError}
                </div>
              )}

              <div className="flex gap-3">
                <button
                  onClick={closeClearModal}
                  className="flex-1 px-4 py-3 border border-border text-foreground rounded-lg hover:bg-muted transition-colors"
                  disabled={clearLoading}
                >
                  {t('account.actions.cancel')}
                </button>
                <button
                  onClick={handleClearAll}
                  disabled={clearConfirmText !== 'DELETE' || clearLoading}
                  className="flex-1 bg-destructive text-destructive-foreground px-4 py-3 rounded-lg font-semibold hover:bg-destructive/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  data-testid="confirm-clear-all"
                >
                  {clearLoading ? t('account.dangerZone.clearing') : t('account.modals.clearFlashcardsConfirm')}
                </button>
              </div>
            </div>
          </div>
        )}

        {showLangWarning && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-card rounded-xl shadow-xl border border-border w-full max-w-md p-6">
              <h3 className="text-lg font-bold text-foreground mb-4">{t('account.modals.switchLanguageTitle')}</h3>
              <p className="text-sm text-muted-foreground mb-4">
                {t('account.modals.switchLanguageWarning', {
                  count: langWarningCount,
                  native: user.nativeLanguage,
                  study: user.studyLanguage,
                })}
              </p>
              <p className="text-sm text-muted-foreground mb-6">
                {t('account.modals.switchLanguageHideNote')}
              </p>
              <div className="flex gap-3">
                <button
                  onClick={cancelLanguageSwitch}
                  className="flex-1 px-4 py-3 border border-border text-foreground rounded-lg hover:bg-muted transition-colors"
                >
                  {t('account.actions.cancel')}
                </button>
                <button
                  onClick={confirmLanguageSwitch}
                  className="flex-1 bg-primary text-primary-foreground px-4 py-3 rounded-lg font-semibold hover:bg-primary/90 transition-colors"
                  data-testid="confirm-lang-switch"
                >
                  {t('account.modals.switchLanguageConfirm')}
                </button>
              </div>
            </div>
          </div>
        )}

        {showDeleteAccountModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-card rounded-xl shadow-xl border border-border w-full max-w-md p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-bold text-destructive">{t('account.modals.deleteAccountTitle')}</h3>
                <button
                  onClick={closeDeleteAccountModal}
                  className="text-muted-foreground hover:text-foreground"
                  disabled={deleteAccountLoading}
                >
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <p className="text-sm text-muted-foreground mb-4">
                {t('account.modals.deleteAccountWarningWeb')}
              </p>

              <p className="text-sm text-foreground mb-2">
                {t('account.modals.typeDeleteToConfirm')}
              </p>
              <input
                type="text"
                value={deleteAccountConfirmText}
                onChange={(e) => setDeleteAccountConfirmText(e.target.value)}
                placeholder={t('account.modals.deletePlaceholder')}
                className="w-full px-4 py-3 bg-background border border-border rounded-lg focus:ring-2 focus:ring-destructive focus:border-transparent text-foreground mb-4"
                disabled={deleteAccountLoading}
                data-testid="delete-account-confirm-input"
              />

              {deleteAccountError && (
                <div className="bg-destructive/20 border border-destructive text-destructive-foreground px-3 py-2 rounded text-sm mb-4">
                  {deleteAccountError}
                </div>
              )}

              <div className="flex gap-3">
                <button
                  onClick={closeDeleteAccountModal}
                  className="flex-1 px-4 py-3 border border-border text-foreground rounded-lg hover:bg-muted transition-colors"
                  disabled={deleteAccountLoading}
                >
                  {t('account.actions.cancel')}
                </button>
                <button
                  onClick={handleDeleteAccount}
                  disabled={deleteAccountConfirmText !== 'DELETE' || deleteAccountLoading}
                  className="flex-1 bg-destructive text-destructive-foreground px-4 py-3 rounded-lg font-semibold hover:bg-destructive/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  data-testid="confirm-delete-account"
                >
                  {deleteAccountLoading ? t('account.dangerZone.deleting') : t('account.modals.confirm')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AccountPage;
